import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FaceIdentityBrandLogo from '../../face-identity/components/FaceIdentityBrandLogo';
import FaceIdentityFlowNav from '../../face-identity/components/FaceIdentityFlowNav';
import {
  getFaceUserProfile,
  persistFaceProfileSnapshot,
} from '../../face-identity/utils/faceIdentityApi';
import { buildKioskNavState } from '../../face-identity/utils/kioskNavState';
import { resolveFacePortraitUrl } from '../../face-identity/utils/facePortrait';
import { useFaceIdentityOrgLogo } from '../../face-identity/hooks/useFaceIdentityOrgLogo';
import { useFaceIdentityScrollShell } from '../../face-identity/hooks/useFaceIdentityScrollShell';
import { lookupCheckinByCode } from '../../salon-admin/utils/checkinQueueApi';
import { SALON_SERVICE_LOBBY_PATH } from '../config/salonApp';
import { captureDeviceInfo } from '../utils/deviceCapture';
import { clearSalonFaceSession, setSalonFaceUserId, setSalonComparePrevious, setSalonFaceOrg, setSalonEntrySource, setSalonPendingCheckin } from '../utils/salonFaceSession';
import { setSalonGuestPii, purgeLegacySalonPiiSessionKeys } from '../utils/salonGuestPiiStore';
import { resolveSalonHomePath } from '../utils/salonHomeNav';
import {
  handoffFromCheckin,
  persistSelectedServicesForRecommendations,
} from '../utils/visitHandoff';
import '../styles/EnterpriseSalonLuxTheme.css';
import '../../face-identity/styles/FaceIdentityLuxTheme.css';
import '../../face-identity/styles/FaceIdentityScrollShell.css';
import './SalonCustomerCodePage.css';

function seedSessionFromCheckin(checkin, { allowMissingFace = false, captureUrl = '' } = {}) {
  const snap = checkin?.customer_snapshot || {};
  const name = checkin?.customer_name || snap.name || snap.full_name || '';
  const mobile = checkin?.mobile || snap.mobile || snap.phone || '';
  const faceUserId =
    checkin?.face_user_id ||
    snap.face_user_id ||
    snap.faceUserId ||
    snap.user?.id ||
    '';
  const email = snap.email || '';
  const gender = snap.gender || '';
  const dob = snap.date_of_birth || snap.dob || '';
  const age = snap.age != null ? snap.age : '';
  const org = checkin?.organization_id || snap.organization_id || '';
  const isNew = Boolean(snap.is_new_member);
  const selectedServices = checkin?.selected_services || snap.selected_services || [];
  const lastSelectedServices =
    checkin?.last_selected_services ||
    snap.last_selected_services ||
    snap.lastSelectedServices ||
    [];
  const portraitUrl =
    captureUrl ||
    resolveFacePortraitUrl(snap, checkin, snap.user, snap.profile);

  if (!faceUserId && !allowMissingFace) {
    const err = new Error(
      'This check-in code is not linked to a face profile. Enroll the guest to continue.',
    );
    err.code = 'MISSING_FACE_USER';
    throw err;
  }

  // Drop previous guest's in-memory kiosk session before binding this check-in.
  clearSalonFaceSession();
  handoffFromCheckin(checkin);
  persistSelectedServicesForRecommendations(selectedServices);

  if (faceUserId) {
    const profileSnapshot = {
      faceUserId,
      user: {
        id: faceUserId,
        name,
        full_name: name,
        mobile,
        email,
        gender,
        age,
        date_of_birth: dob,
        organization_id: org,
        last_salon_reference: snap.last_salon_reference || '',
        profile: snap.profile || {},
        image: portraitUrl || undefined,
      },
      profile: snap.profile || {},
      selectedServices,
      lastSelectedServices,
      checkedInAt: checkin?.checked_in_at || new Date().toISOString(),
      isNewMember: isNew,
      checkinCode: checkin?.customer_code,
      checkinId: checkin?.id,
      captureUrl: portraitUrl || undefined,
    };
    persistFaceProfileSnapshot(profileSnapshot);

    setSalonFaceUserId(faceUserId);
    setSalonComparePrevious(!isNew);
    setSalonGuestPii({ mobile, name, email, gender, dob, age });
    purgeLegacySalonPiiSessionKeys();
    if (org) setSalonFaceOrg(org);
    setSalonEntrySource('customer_code');

    return profileSnapshot;
  }

  setSalonPendingCheckin({
    id: checkin.id,
    code: checkin.customer_code || '',
  });
  if (mobile || name) {
    setSalonGuestPii({ mobile, name });
    purgeLegacySalonPiiSessionKeys();
  }
  setSalonEntrySource('customer_code');

  return {
    faceUserId: '',
    user: { name, full_name: name, mobile, organization_id: org },
    selectedServices,
    lastSelectedServices,
    isNewMember: true,
    checkinCode: checkin?.customer_code,
    checkinId: checkin?.id,
    needsFaceEnroll: true,
    captureUrl: portraitUrl || undefined,
  };
}

export default function SalonCustomerCodePage() {
  useFaceIdentityScrollShell();
  const navigate = useNavigate();
  const location = useLocation();
  const { orgLogoUrl, showLogo, onLogoError } = useFaceIdentityOrgLogo();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [missingFaceLink, setMissingFaceLink] = useState(false);
  const [pendingCheckin, setPendingCheckin] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const goEnrollFace = (checkin) => {
    const selectedServices = checkin?.selected_services || [];
    navigate('/naturals/salon/scan', {
      replace: true,
      state: {
        mode: 'salon',
        next: SALON_SERVICE_LOBBY_PATH,
        pendingCheckinId: checkin?.id,
        pendingCheckinCode: checkin?.customer_code,
        preselectedServices: selectedServices,
        guestName: checkin?.customer_name || '',
        guestMobile: checkin?.mobile || '',
        token: location.state?.token || null,
        deviceInfo: location.state?.deviceInfo || captureDeviceInfo(),
      },
    });
  };

  const onLookup = async (event) => {
    event.preventDefault();
    const trimmed = String(code || '').replace(/\D/g, '').slice(0, 4);
    if (trimmed.length !== 4) {
      setError('Enter the 4-digit check-in code');
      setMissingFaceLink(false);
      setPendingCheckin(null);
      return;
    }
    setError('');
    setMissingFaceLink(false);
    setPendingCheckin(null);
    setSubmitting(true);
    try {
      const checkin = await lookupCheckinByCode(trimmed, { complete: false });
      try {
        const faceUserId =
          checkin?.face_user_id ||
          checkin?.customer_snapshot?.face_user_id ||
          checkin?.customer_snapshot?.faceUserId ||
          '';
        let portraitUrl = resolveFacePortraitUrl(checkin?.customer_snapshot, checkin);
        if (!portraitUrl && faceUserId) {
          try {
            const profileData = await getFaceUserProfile(faceUserId);
            portraitUrl = resolveFacePortraitUrl(
              profileData?.user,
              profileData?.profile,
              profileData,
            );
          } catch (_) {
            /* portrait optional */
          }
        }
        const snapshot = seedSessionFromCheckin(checkin, {
          allowMissingFace: false,
          captureUrl: portraitUrl,
        });
        const deviceInfo = location.state?.deviceInfo || captureDeviceInfo();
        navigate('/naturals/salon/welcome', {
          replace: true,
          state: {
            user: snapshot.user,
            profile: snapshot.profile,
            isNewMember: snapshot.isNewMember,
            faceUserId: snapshot.faceUserId,
            fromCustomerCode: true,
            checkinCode: checkin.customer_code,
            checkinId: checkin.id,
            preselectedServices: snapshot.selectedServices || [],
            lastSelectedServices: snapshot.lastSelectedServices || [],
            captureUrl: snapshot.captureUrl || portraitUrl || undefined,
            next: SALON_SERVICE_LOBBY_PATH,
            mode: 'salon',
            token: location.state?.token || null,
            deviceInfo,
          },
        });
      } catch (seedErr) {
        if (seedErr?.code === 'MISSING_FACE_USER') {
          seedSessionFromCheckin(checkin, { allowMissingFace: true });
          setMissingFaceLink(true);
          setPendingCheckin(checkin);
          setError(
            'Code found, but no face profile yet. Enroll the guest to keep their selected services.',
          );
          return;
        }
        throw seedErr;
      }
    } catch (err) {
      setError(err?.message || 'Invalid check-in code');
      setMissingFaceLink(err?.code === 'MISSING_FACE_USER');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="salon-code-page page-bg fi-lux-theme fi-scroll-page">
      <div className="salon-code-page__stage">
        <header className="salon-code-page__brand">
          <FaceIdentityBrandLogo
            showLogo={showLogo}
            orgLogoUrl={orgLogoUrl}
            onLogoError={onLogoError}
            imgClassName="salon-code-page__logo"
            fallbackClassName="salon-code-page__logo-text"
          />
          <FaceIdentityFlowNav
            onBack={() => {
              clearSalonFaceSession();
              navigate(SALON_SERVICE_LOBBY_PATH, {
                replace: true,
                state: buildKioskNavState(location.state, { mode: 'salon' }),
              });
            }}
            homePath={resolveSalonHomePath()}
          />
        </header>

        <form className="salon-code-page__form" onSubmit={onLookup}>
          <p className="salon-code-page__eyebrow">Customer code</p>
          <h1 className="salon-code-page__title">Enter check-in code</h1>
          <p className="salon-code-page__lede">
            Ask the guest for the 4-digit code shown on the partner check-in card.
          </p>

          <label className="salon-code-page__label">
            <span className="sr-only">4-digit code</span>
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={4}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
              className="salon-code-page__input"
              placeholder="••••"
              disabled={submitting}
            />
          </label>

          {error ? <p className="salon-code-page__error">{error}</p> : null}

          {missingFaceLink && pendingCheckin ? (
            <button
              type="button"
              className="salon-code-page__submit"
              onClick={() => goEnrollFace(pendingCheckin)}
            >
              Enroll face &amp; continue
            </button>
          ) : null}

          <button type="submit" className="salon-code-page__submit" disabled={submitting}>
            {submitting ? 'Checking…' : 'Confirm'}
          </button>

          <button
            type="button"
            className="salon-code-page__back"
            onClick={() => {
              clearSalonFaceSession();
              navigate(SALON_SERVICE_LOBBY_PATH, {
                replace: true,
                state: buildKioskNavState(location.state, { mode: 'salon' }),
              });
            }}
            disabled={submitting}
          >
            Back
          </button>
        </form>
      </div>
    </div>
  );
}
